/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { collectTextFontUsage } from '../elements/text/utils/mixedTypography';

/**
 * Returns a list of font names used across all pages in a story.
 *
 * @param {Array<import('../types').Page>} pages List of pages.
 * @return {Array<string>} Font names.
 */
export function getInUseFontsForPages(pages) {
  return (
    Array.from(
      new Set(
        pages
          .map(({ elements = [] }) =>
            elements.flatMap((element) => {
              if (element.type === 'text' && Boolean(element.font?.family)) {
                return collectTextFontUsage(element).map(
                  ({ font }) => font.family
                );
              }
              return [];
            })
          )
          .flat()
          .filter(Boolean)
      )
    ) || []
  );
}

/**
 * Returns a list of text sets that include the fonts provided in the options object.
 *
 * @param {Array<Object>} options Object containing an array of fonts and text sets to filter.
 * @return {Array<Object>} List of text sets that match the array of fonts.
 */
export function getTextSetsForFonts({ fonts, textSets }) {
  return textSets
    .map((currentTextSet) => {
      const hasFontInUse = currentTextSet.elements.reduce(
        (elementMemo, currentElement) => {
          if (
            currentElement.type === 'text' &&
            Boolean(currentElement.font?.family)
          ) {
            const elementFonts = collectTextFontUsage(currentElement).map(
              ({ font }) => font.family
            );
            return (
              elementMemo || elementFonts.some((font) => fonts.includes(font))
            );
          }
          return elementMemo;
        },
        false
      );
      return hasFontInUse && currentTextSet;
    })
    .filter(Boolean);
}
